import React, { useMemo, useState } from 'react';

import searchIcon from 'assets/images/svg/search.svg';

import { Container, CrossIcon, FilterBar, Image } from '../../../components';
import styles from './FAQ.module.scss';
import { faqCategories, FaqCategory, FaqCategoryId, faqQuestionsTotal } from './faqData';
import { FaqList } from './FaqList';

const ALL_CATEGORIES = 'all';

type ActiveCategory = FaqCategoryId | typeof ALL_CATEGORIES;

// Ответы хранятся как HTML, поэтому для поиска по тексту убираем разметку.
const stripTags = (html: string): string => html.replace(/<[^>]*>/g, ' ');

const matches = (haystack: string, query: string): boolean =>
  haystack.toLowerCase().includes(query);

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(ALL_CATEGORIES);
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();

  const filteredCategories = useMemo((): Array<FaqCategory> => {
    return faqCategories
      .filter((category) => activeCategory === ALL_CATEGORIES || category.id === activeCategory)
      .map((category) => ({
        ...category,
        questions: query
          ? category.questions.filter(
              ({ title, content }) => matches(title, query) || matches(stripTags(content), query)
            )
          : category.questions,
      }))
      .filter((category) => category.questions.length > 0);
  }, [activeCategory, query]);

  const foundCount = filteredCategories.reduce(
    (total, category) => total + category.questions.length,
    0
  );

  const getCategoryCount = (category: FaqCategory): number =>
    query
      ? category.questions.filter(
          ({ title, content }) => matches(title, query) || matches(stripTags(content), query)
        ).length
      : category.questions.length;

  const resetFilters = (): void => {
    setActiveCategory(ALL_CATEGORIES);
    setSearch('');
  };

  return (
    <Container>
      <div className={styles.page}>
        <header className={styles.hero}>
          <h1 className={styles.title}>Часто задаваемые вопросы</h1>
          <p className={styles.lead}>
            Ответы на вопросы о том, как устроена платформа, как заключаются сделки и как продать
            долговое требование. Выберите раздел или воспользуйтесь поиском.
          </p>
        </header>

        <div className={styles.toolbar}>
          <FilterBar className={styles.filterBar}>
            <FilterBar.Pills>
              <FilterBar.Pill
                active={activeCategory === ALL_CATEGORIES}
                onClick={() => setActiveCategory(ALL_CATEGORIES)}
              >
                Все вопросы ({faqQuestionsTotal})
              </FilterBar.Pill>
              {faqCategories.map((category) => (
                <FilterBar.Pill
                  key={category.id}
                  active={activeCategory === category.id}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.label} ({getCategoryCount(category)})
                </FilterBar.Pill>
              ))}
            </FilterBar.Pills>
            <FilterBar.Actions>
              <label className={styles.search}>
                <Image src={searchIcon} alt="" className={styles.searchIcon} />
                <input
                  type="search"
                  className={styles.searchInput}
                  placeholder="Поиск по вопросам"
                  aria-label="Поиск по вопросам"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className={styles.searchClear}
                    onClick={() => setSearch('')}
                    aria-label="Очистить поиск"
                  >
                    <CrossIcon size={18} preset="circle-muted" />
                  </button>
                )}
              </label>
            </FilterBar.Actions>
          </FilterBar>
          {query && foundCount > 0 && (
            <p className={styles.resultsHint} role="status">
              Найдено вопросов: {foundCount}
            </p>
          )}
        </div>

        {foundCount > 0 ? (
          <FaqList categories={filteredCategories} search={search} />
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>По вашему запросу ничего не найдено</p>
            <p className={styles.emptyText}>
              Попробуйте изменить формулировку или сбросить фильтры.
            </p>
            <button type="button" className={styles.emptyButton} onClick={resetFilters}>
              Показать все вопросы
            </button>
          </div>
        )}

        <aside className={styles.contacts}>
          <div>
            <p className={styles.contactsTitle}>Не нашли ответ на свой вопрос?</p>
            <p className={styles.contactsText}>
              Напишите нам — специалисты поддержки помогут разобраться.
            </p>
          </div>
          <a className={styles.contactsLink} href="mailto:info@conomica.ru">
            info@conomica.ru
          </a>
        </aside>
      </div>
    </Container>
  );
};

export default FAQ;
